import {
  getCsvValues,
  getValidTimestamp,
  HydratedUrlFilters,
  isSafeOptionValue,
  toCanonicalSearch,
  uniqueValues,
} from 'views/bpaUrlFilterUtils';

import { FilterValues } from './types/integrationListing.types';

export const BPA_INTEGRATION_DATE_FILTER_KEYS = [
  'created_at',
  'updated_at',
  'last_sync_at',
] as const;

const TEXT_LIST_FILTER_KEYS = ['provider', 'module'] as const;

const INTEGRATION_TYPE_MAP: Record<string, string> = {
  account_level: 'ACCOUNT_LEVEL',
  organization_level: 'ORGANIZATION_LEVEL',
};

const VALID_STATUSES = new Set(['active', 'aging', 'deleted']);

export const getDefaultBPAIntegrationFilters = (): FilterValues => ({
  status: ['active'],
});

const getLastValue = (params: URLSearchParams, key: string): string | null => {
  const values = params.getAll(key);
  return values.length > 0 ? values[values.length - 1] : null;
};

export const parseBPAIntegrationUrlFilters = (
  search: string,
): HydratedUrlFilters<FilterValues> => {
  const params = new URLSearchParams(search);
  const canonicalParams = new URLSearchParams();
  const queryFilters: FilterValues = {};
  let hasValidUrlFilters = false;

  const markValid = () => {
    hasValidUrlFilters = true;
  };

  const searchValue = getLastValue(params, 'search')?.trim();
  if (searchValue && searchValue.length <= 250) {
    queryFilters.search = searchValue;
    canonicalParams.set('search', searchValue);
    markValid();
  }

  TEXT_LIST_FILTER_KEYS.forEach((key) => {
    const values = uniqueValues(
      getCsvValues(params, key).filter((value) => isSafeOptionValue(value)),
    );
    if (values.length > 0) {
      queryFilters[key] = values;
      canonicalParams.set(key, values.join(','));
      markValid();
    }
  });

  const integrationTypes = uniqueValues(
    getCsvValues(params, 'integration_type')
      .map((value) => INTEGRATION_TYPE_MAP[value.toLowerCase()])
      .filter((value): value is string => Boolean(value)),
  );
  if (integrationTypes.length > 0) {
    queryFilters.integration_type = integrationTypes;
    canonicalParams.set('integration_type', integrationTypes.join(','));
    markValid();
  }

  const rawStatuses = getCsvValues(params, 'status');
  const explicitlyAllStatuses =
    params.has('status') &&
    (rawStatuses.length === 0 ||
      rawStatuses.some((value) => value.toLowerCase() === 'all'));
  const statuses = uniqueValues(
    rawStatuses
      .map((value) => value.toLowerCase())
      .filter((value) => VALID_STATUSES.has(value)),
  );
  if (statuses.length > 0) {
    queryFilters.status = statuses;
    canonicalParams.set('status', statuses.join(','));
    markValid();
  } else if (explicitlyAllStatuses) {
    queryFilters.status = [''];
    canonicalParams.set('status', 'all');
    markValid();
  }

  const accountId = getLastValue(params, 'account_id')?.trim();
  if (accountId && /^\d+$/.test(accountId)) {
    const parsedAccountId = Number(accountId);
    if (Number.isSafeInteger(parsedAccountId) && parsedAccountId > 0) {
      queryFilters.account_id = accountId;
      canonicalParams.set('account_id', accountId);
      markValid();
    }
  }

  BPA_INTEGRATION_DATE_FILTER_KEYS.forEach((key) => {
    const fromKey = `dt_filter__${key}__from`;
    const toKey = `dt_filter__${key}__to`;
    const from = getValidTimestamp(getLastValue(params, fromKey));
    const to = getValidTimestamp(getLastValue(params, toKey));

    if (from === null && to === null) return;
    if (from !== null && to !== null && from > to) return;

    queryFilters[key] = [{ from, to }];
    if (from !== null) canonicalParams.set(fromKey, String(from));
    if (to !== null) canonicalParams.set(toKey, String(to));
    markValid();
  });

  return {
    filterValues: hasValidUrlFilters
      ? queryFilters
      : getDefaultBPAIntegrationFilters(),
    routeFilter: undefined,
    hasValidUrlFilters,
    canonicalSearch: toCanonicalSearch(canonicalParams),
  };
};
